import { useSeoMeta } from '@unhead/react';
import { useState, useCallback, useRef } from 'react';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { useNostrPublish } from '@/hooks/useNostrPublish';
import { useToast } from '@/hooks/useToast';
import { LoginArea } from '@/components/auth/LoginArea';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Checkbox } from '@/components/ui/checkbox';
import { Textarea } from '@/components/ui/textarea';
import { ScrollArea } from '@/components/ui/scroll-area';
import { autoParseFile, parsePlainText, type ImportedPost } from '@/lib/importParsers';
import { fetchBlueskyPosts } from '@/lib/blueskyApi';
import { markAsMigrated, countAlreadyMigrated, wasAlreadyMigrated } from '@/lib/migrationHistory';
import { cn } from '@/lib/utils';
import { Input } from '@/components/ui/input';
import { useAppContext } from '@/hooks/useAppContext';
import { useTheme } from '@/hooks/useTheme';

type Step = 'welcome' | 'import' | 'preview' | 'publish' | 'done';

const PLATFORMS = [
  { id: 'twitter', label: 'Twitter / X', icon: '𝕏', description: 'Upload your tweets.js from a Twitter data archive', accept: '.js,.json' },
  { id: 'facebook', label: 'Facebook', icon: '📘', description: 'Upload your_posts_1.json from a Facebook data export', accept: '.json' },
  { id: 'linkedin', label: 'LinkedIn', icon: '💼', description: 'Upload Shares.csv from a LinkedIn data export', accept: '.csv' },
  { id: 'bluesky', label: 'Bluesky', icon: '🦋', description: 'Enter your handle — we fetch your posts from the public API', accept: '' },
  { id: 'reddit', label: 'Reddit', icon: '🤖', description: 'Upload posts.csv or comments.csv from a Reddit data export', accept: '.csv' },
  { id: 'telegram', label: 'Telegram', icon: '✈️', description: 'Upload result.json from a Telegram Desktop export', accept: '.json' },
  { id: 'mastodon', label: 'Mastodon', icon: '🐘', description: 'Upload your outbox.json from a Mastodon export', accept: '.json' },
  { id: 'csv', label: 'CSV / Spreadsheet', icon: '📊', description: 'Upload a CSV file with one post per row', accept: '.csv' },
  { id: 'text', label: 'Plain Text', icon: '📝', description: 'Paste or upload a text file — one post per line', accept: '.txt' },
];

export default function Index() {
  useSeoMeta({
    title: 'BCH Nostr Migrate — Bring Your Content to Freedom',
    description: 'Migrate your social media posts from Twitter/X, Facebook, LinkedIn, Bluesky, Reddit, Mastodon, and more to the BCH Nostr decentralised network.',
  });

  const { user } = useCurrentUser();
  const { mutateAsync: publishEvent } = useNostrPublish();
  const { toast } = useToast();
  const { config } = useAppContext();
  const { theme, setTheme } = useTheme();

  const [step, setStep] = useState<Step>('welcome');
  const [selectedPlatform, setSelectedPlatform] = useState<string | null>(null);
  const [posts, setPosts] = useState<ImportedPost[]>([]);
  const [pasteText, setPasteText] = useState('');
  const [isDragging, setIsDragging] = useState(false);
  const [fileName, setFileName] = useState('');
  const [publishing, setPublishing] = useState(false);
  const [publishProgress, setPublishProgress] = useState(0);
  const [publishedCount, setPublishedCount] = useState(0);
  const [failedCount, setFailedCount] = useState(0);
  const [bskyHandle, setBskyHandle] = useState('');
  const [bskyLoading, setBskyLoading] = useState(false);
  const [speedMode, setSpeedMode] = useState<'normal' | 'slow'>('normal');
  const [duplicateCount, setDuplicateCount] = useState(0);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const selectedCount = posts.filter((p) => p.selected).length;

  // ── Import ──────────────────────────────────────────────────────────────────

  const applyDedupAndPreview = useCallback((parsed: ImportedPost[]) => {
    const dupCount = countAlreadyMigrated(parsed);
    setDuplicateCount(dupCount);
    if (dupCount > 0) {
      toast({ title: `${dupCount} duplicate${dupCount > 1 ? 's' : ''} found`, description: 'Posts you already migrated are deselected.' });
    }
    const withDedup = parsed.map((p) => ({
      ...p,
      selected: !wasAlreadyMigrated(p.content, p.createdAt),
    }));
    setPosts(withDedup);
    setStep('preview');
  }, [toast]);

  const handleFile = useCallback((file: File) => {
    setFileName(file.name);
    const reader = new FileReader();
    reader.onload = (e) => {
      const content = e.target?.result as string;
      const parsed = autoParseFile(file.name, content);
      if (parsed.length === 0) {
        toast({ title: 'No posts found', description: 'Could not find any posts in this file. Try plain text or CSV.', variant: 'destructive' });
        return;
      }
      applyDedupAndPreview(parsed);
    };
    reader.readAsText(file);
  }, [toast, applyDedupAndPreview]);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files[0];
    if (file) handleFile(file);
  }, [handleFile]);

  const handlePaste = useCallback(() => {
    if (!pasteText.trim()) return;
    const parsed = parsePlainText(pasteText);
    setFileName('pasted text');
    applyDedupAndPreview(parsed);
  }, [pasteText, applyDedupAndPreview]);

  // ── Bluesky API fetch ────────────────────────────────────────────────────────

  const handleBlueskyFetch = useCallback(async () => {
    if (!bskyHandle.trim()) return;
    setBskyLoading(true);
    try {
      const parsed = await fetchBlueskyPosts(bskyHandle.trim());
      if (parsed.length === 0) {
        toast({ title: 'No posts found', description: 'This account has no public posts, or the handle is incorrect.', variant: 'destructive' });
        return;
      }
      setFileName(`@${bskyHandle.trim()}`);
      applyDedupAndPreview(parsed);
    } catch (err) {
      toast({ title: 'Failed to fetch', description: err instanceof Error ? err.message : 'Could not reach Bluesky API.', variant: 'destructive' });
    } finally {
      setBskyLoading(false);
    }
  }, [bskyHandle, toast, applyDedupAndPreview]);

  // ── Selection ───────────────────────────────────────────────────────────────

  const togglePost = (id: string) => {
    setPosts((prev) => prev.map((p) => p.id === id ? { ...p, selected: !p.selected } : p));
  };

  const toggleAll = () => {
    const allSelected = posts.every((p) => p.selected);
    setPosts((prev) => prev.map((p) => ({ ...p, selected: !allSelected })));
  };

  // ── Publish ─────────────────────────────────────────────────────────────────

  const handlePublish = async () => {
    if (!user) return;
    const toPublish = posts.filter((p) => p.selected);
    if (toPublish.length === 0) return;

    setPublishing(true);
    setPublishProgress(0);
    setPublishedCount(0);
    setFailedCount(0);
    setStep('publish');

    let ok = 0;
    let fail = 0;

    const delay = speedMode === 'slow' ? 500 : 150;

    for (let i = 0; i < toPublish.length; i++) {
      const post = toPublish[i];
      try {
        await publishEvent({
          kind: 1,
          content: post.content,
          created_at: post.createdAt,
          tags: [['alt', `Migrated from ${post.source}`]],
        });
        markAsMigrated(post.content, post.createdAt);
        ok++;
      } catch {
        fail++;
      }
      setPublishedCount(ok);
      setFailedCount(fail);
      setPublishProgress(Math.round(((i + 1) / toPublish.length) * 100));

      if (i < toPublish.length - 1) {
        await new Promise((r) => setTimeout(r, delay));
      }
    }

    setPublishing(false);
    setStep('done');
  };

  // ── Reset ───────────────────────────────────────────────────────────────────

  const reset = () => {
    setStep('welcome');
    setSelectedPlatform(null);
    setPosts([]);
    setPasteText('');
    setFileName('');
    setPublishProgress(0);
    setPublishedCount(0);
    setFailedCount(0);
    setBskyHandle('');
    setDuplicateCount(0);
  };

  // ── Render ──────────────────────────────────────────────────────────────────

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="border-b bg-card sticky top-0 z-50 shadow-sm">
        <div className="container mx-auto flex items-center justify-between h-16 px-4">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-full bg-primary flex items-center justify-center text-primary-foreground font-black text-lg select-none">N</div>
            <div className="leading-tight">
              <div className="font-bold text-base text-foreground tracking-tight">BCH Nostr Migrate</div>
              <div className="text-xs text-muted-foreground">Bring your content to freedom</div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setTheme(theme === 'dark' ? 'light' : 'dark')}
              className="inline-flex items-center justify-center w-9 h-9 rounded-lg border border-border hover:bg-accent transition-colors text-muted-foreground hover:text-foreground"
              aria-label="Toggle dark mode"
            >
              {theme === 'dark' ? (
                <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="4"/><path d="M12 2v2"/><path d="M12 20v2"/><path d="m4.93 4.93 1.41 1.41"/><path d="m17.66 17.66 1.41 1.41"/><path d="M2 12h2"/><path d="M20 12h2"/><path d="m6.34 17.66-1.41 1.41"/><path d="m19.07 4.93-1.41 1.41"/></svg>
              ) : (
                <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 3a6 6 0 0 0 9 9 9 9 0 1 1-9-9Z"/></svg>
              )}
            </button>
            <LoginArea className="max-w-48" />
          </div>
        </div>
      </header>

      {/* Step indicator */}
      {step !== 'welcome' && (
        <div className="bg-secondary/40 border-b">
          <div className="container mx-auto px-4 py-2">
            <StepIndicator current={step} />
          </div>
        </div>
      )}

      <main className="container mx-auto px-4 py-8 max-w-3xl">

        {/* ── Welcome ── */}
        {step === 'welcome' && (
          <div className="space-y-8">
            {/* Hero */}
            <div className="text-center space-y-4 py-6">
              <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-primary/10 border-2 border-primary mb-2">
                <span className="text-4xl">🌿</span>
              </div>
              <h1 className="text-4xl font-extrabold text-foreground tracking-tight">
                Move to <span className="text-primary">BCH Nostr</span>
              </h1>
              <p className="text-lg text-muted-foreground max-w-xl mx-auto">
                Your content belongs to you. Import your posts from Twitter/X, Facebook, LinkedIn, Bluesky, Reddit, Mastodon, or any platform and republish them on the censorship-resistant BCH Nostr network in minutes.
              </p>
              {!user && (
                <div className="flex flex-col items-center gap-2 pt-2">
                  <p className="text-sm text-muted-foreground">Sign in to publish your content</p>
                  <LoginArea className="max-w-56" />
                </div>
              )}
            </div>

            {/* Feature cards */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              {[
                { icon: '🔒', title: 'You Own It', desc: 'Your posts are signed with your private key — no platform can delete them.' },
                { icon: '⚡', title: 'Instant Transfer', desc: 'Import, preview, and publish your archive in just a few clicks.' },
                { icon: '💚', title: 'BCH Powered', desc: 'Published to BCH Nostr relays — combining decentralised social with Bitcoin Cash.' },
              ].map((f) => (
                <Card key={f.title} className="border-border/60 hover:border-primary/40 transition-colors">
                  <CardContent className="pt-5 pb-4 text-center space-y-2">
                    <div className="text-3xl">{f.icon}</div>
                    <div className="font-semibold text-foreground">{f.title}</div>
                    <div className="text-sm text-muted-foreground">{f.desc}</div>
                  </CardContent>
                </Card>
              ))}
            </div>

            {/* Platform selection — grouped */}
            <Card>
              <CardHeader>
                <CardTitle>Choose your platform</CardTitle>
                <CardDescription>Where is your content coming from?</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Popular platforms */}
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                  {PLATFORMS.filter((p) => !['csv', 'text'].includes(p.id)).map((p) => (
                    <button
                      key={p.id}
                      onClick={() => { setSelectedPlatform(p.id); setStep('import'); }}
                      className={cn(
                        'flex flex-col items-center gap-1.5 rounded-xl border p-3 text-center transition-all hover:border-primary hover:bg-primary/5',
                        selectedPlatform === p.id ? 'border-primary bg-primary/10' : 'border-border'
                      )}
                    >
                      <span className="text-2xl">{p.icon}</span>
                      <span className="font-medium text-sm text-foreground">{p.label}</span>
                    </button>
                  ))}
                </div>
                {/* Other formats */}
                <div className="flex gap-2">
                  {PLATFORMS.filter((p) => ['csv', 'text'].includes(p.id)).map((p) => (
                    <button
                      key={p.id}
                      onClick={() => { setSelectedPlatform(p.id); setStep('import'); }}
                      className={cn(
                        'flex items-center gap-2 rounded-lg border px-3 py-2 text-sm transition-all hover:border-primary hover:bg-primary/5 flex-1',
                        selectedPlatform === p.id ? 'border-primary bg-primary/10' : 'border-border'
                      )}
                    >
                      <span>{p.icon}</span>
                      <span className="font-medium text-foreground">{p.label}</span>
                    </button>
                  ))}
                </div>
              </CardContent>
            </Card>

            <p className="text-center text-xs text-muted-foreground">
              Vibed with{' '}
              <a href="https://shakespeare.diy" target="_blank" rel="noopener noreferrer" className="underline hover:text-primary">
                Shakespeare
              </a>
            </p>
          </div>
        )}

        {/* ── Import ── */}
        {step === 'import' && (
          <div className="space-y-6">
            <div className="flex items-center gap-3">
              <Button variant="ghost" size="sm" onClick={() => setStep('welcome')}>← Back</Button>
              <h2 className="text-2xl font-bold">Import your content</h2>
            </div>

            {selectedPlatform === 'bluesky' ? (
              <Card>
                <CardHeader>
                  <CardTitle>Enter your Bluesky handle</CardTitle>
                  <CardDescription>We'll fetch your public posts directly — no file needed</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <Input
                    placeholder="yourname.bsky.social"
                    value={bskyHandle}
                    onChange={(e) => setBskyHandle(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') handleBlueskyFetch(); }}
                  />
                  <Button onClick={handleBlueskyFetch} disabled={!bskyHandle.trim() || bskyLoading} className="w-full">
                    {bskyLoading ? 'Fetching posts…' : 'Fetch posts'}
                  </Button>
                </CardContent>
              </Card>
            ) : selectedPlatform === 'text' ? (
              <Card>
                <CardHeader>
                  <CardTitle>Paste your posts</CardTitle>
                  <CardDescription>One post per line — or upload a .txt file below</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <Textarea
                    placeholder="Paste your posts here, one per line..."
                    className="min-h-48 font-mono text-sm"
                    value={pasteText}
                    onChange={(e) => setPasteText(e.target.value)}
                  />
                  <div className="flex gap-3">
                    <Button onClick={handlePaste} disabled={!pasteText.trim()} className="flex-1">
                      Preview posts
                    </Button>
                    <Button variant="outline" onClick={() => fileInputRef.current?.click()}>
                      Upload file
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ) : (
              <Card>
                <CardHeader>
                  <CardTitle>
                    Upload your {PLATFORMS.find((p) => p.id === selectedPlatform)?.label} export
                  </CardTitle>
                  <CardDescription>
                    {PLATFORMS.find((p) => p.id === selectedPlatform)?.description}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div
                    className={cn(
                      'border-2 border-dashed rounded-xl p-12 text-center transition-all cursor-pointer',
                      isDragging ? 'border-primary bg-primary/10' : 'border-border hover:border-primary/50 hover:bg-secondary/40'
                    )}
                    onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
                    onDragLeave={() => setIsDragging(false)}
                    onDrop={handleDrop}
                    onClick={() => fileInputRef.current?.click()}
                  >
                    <div className="text-5xl mb-4">📂</div>
                    <p className="font-semibold text-foreground">Drop your file here or click to browse</p>
                    <p className="text-sm text-muted-foreground mt-1">
                      Accepts {PLATFORMS.find((p) => p.id === selectedPlatform)?.accept ?? '.json, .csv, .txt'}
                    </p>
                    {fileName && (
                      <Badge variant="secondary" className="mt-3">{fileName}</Badge>
                    )}
                  </div>
                </CardContent>
              </Card>
            )}

            <input
              ref={fileInputRef}
              type="file"
              className="hidden"
              accept={PLATFORMS.find((p) => p.id === selectedPlatform)?.accept ?? '*'}
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) handleFile(f);
              }}
            />

            {/* How-to guide */}
            <Card className="bg-secondary/30 border-border/50">
              <CardHeader className="pb-2">
                <CardTitle className="text-base">How to export from {PLATFORMS.find((p) => p.id === selectedPlatform)?.label}</CardTitle>
              </CardHeader>
              <CardContent>
                <HowToGuide platform={selectedPlatform ?? ''} />
              </CardContent>
            </Card>
          </div>
        )}

        {/* ── Preview ── */}
        {step === 'preview' && (
          <div className="space-y-6">
            <div className="flex flex-col sm:flex-row sm:items-center gap-3">
              <Button variant="ghost" size="sm" onClick={() => setStep('import')}>← Back</Button>
              <h2 className="text-2xl font-bold">Review your posts</h2>
              <Badge variant="secondary" className="ml-auto">{posts.length} posts found</Badge>
            </div>

            {!user && (
              <Card className="border-primary/40 bg-primary/5">
                <CardContent className="py-4 flex flex-col sm:flex-row items-center gap-4">
                  <div className="flex-1">
                    <p className="font-semibold text-foreground">Sign in to publish</p>
                    <p className="text-sm text-muted-foreground">You need a Nostr account to publish posts to BCH Nostr.</p>
                  </div>
                  <LoginArea className="max-w-44" />
                </CardContent>
              </Card>
            )}

            {/* Migration explainer */}
            <Card className="bg-secondary/30 border-border/50">
              <CardContent className="py-3 px-4 text-sm text-muted-foreground space-y-1">
                <p><strong className="text-foreground">How migration works:</strong> Each selected post will be published as a Nostr note with its <strong className="text-foreground">original timestamp</strong> preserved. This means posts will appear at their original dates in Nostr clients, not today's date.</p>
                {duplicateCount > 0 && (
                  <p className="text-primary">{duplicateCount} post{duplicateCount > 1 ? 's were' : ' was'} already migrated previously and {duplicateCount > 1 ? 'have' : 'has'} been deselected.</p>
                )}
              </CardContent>
            </Card>

            <div className="flex items-center justify-between">
              <button
                onClick={toggleAll}
                className="text-sm text-primary underline underline-offset-2 hover:no-underline"
              >
                {posts.every((p) => p.selected) ? 'Deselect all' : 'Select all'}
              </button>
              <span className="text-sm text-muted-foreground">{selectedCount} of {posts.length} selected</span>
            </div>

            <ScrollArea className="h-[420px] rounded-xl border">
              <div className="divide-y">
                {posts.map((post) => (
                  <div
                    key={post.id}
                    className={cn(
                      'flex gap-3 p-4 transition-colors',
                      post.selected ? 'bg-background' : 'bg-muted/30 opacity-60'
                    )}
                  >
                    <Checkbox
                      checked={post.selected}
                      onCheckedChange={() => togglePost(post.id)}
                      className="mt-1 shrink-0"
                    />
                    <div className="flex-1 min-w-0 space-y-1">
                      <p className="text-sm text-foreground break-words leading-relaxed">{post.content}</p>
                      <div className="flex items-center gap-2">
                        <Badge variant="outline" className="text-xs">{post.source}</Badge>
                        <span className="text-xs text-muted-foreground">{formatDate(post.createdAt)}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </ScrollArea>

            <div className="flex items-center gap-3 text-sm">
              <span className="text-muted-foreground">Publish speed:</span>
              <button
                onClick={() => setSpeedMode(speedMode === 'normal' ? 'slow' : 'normal')}
                className={cn(
                  'rounded-full border px-3 py-1 transition-colors text-xs font-medium',
                  speedMode === 'normal' ? 'border-primary bg-primary/10 text-primary' : 'border-border text-muted-foreground'
                )}
              >
                Normal
              </button>
              <button
                onClick={() => setSpeedMode(speedMode === 'slow' ? 'normal' : 'slow')}
                className={cn(
                  'rounded-full border px-3 py-1 transition-colors text-xs font-medium',
                  speedMode === 'slow' ? 'border-primary bg-primary/10 text-primary' : 'border-border text-muted-foreground'
                )}
              >
                Slow (safer)
              </button>
            </div>

            <div className="flex flex-col sm:flex-row gap-3">
              <Button
                className="flex-1"
                disabled={selectedCount === 0 || !user}
                onClick={handlePublish}
              >
                Publish {selectedCount} post{selectedCount !== 1 ? 's' : ''} to BCH Nostr →
              </Button>
              <Button variant="outline" onClick={() => { setStep('import'); setFileName(''); setPosts([]); }}>
                Re-import
              </Button>
            </div>
          </div>
        )}

        {/* ── Publishing ── */}
        {step === 'publish' && (
          <div className="space-y-6 text-center py-8">
            <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-primary/10 border-2 border-primary">
              <span className="text-4xl">🚀</span>
            </div>
            <h2 className="text-2xl font-bold">Publishing to BCH Nostr…</h2>
            <p className="text-muted-foreground">
              Broadcasting your posts to the BCH Nostr network. Please keep this tab open.
            </p>
            <div className="max-w-md mx-auto space-y-3">
              <Progress value={publishProgress} className="h-3" />
              <p className="text-sm text-muted-foreground">
                {publishedCount + failedCount} / {selectedCount} — {publishProgress}%
              </p>
              {failedCount > 0 && (
                <p className="text-sm text-destructive">{failedCount} failed</p>
              )}
            </div>
          </div>
        )}

        {/* ── Done ── */}
        {step === 'done' && (
          <div className="space-y-8 text-center py-8">
            <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-primary/10 border-2 border-primary">
              <span className="text-4xl">✅</span>
            </div>
            <div className="space-y-2">
              <h2 className="text-3xl font-extrabold text-foreground">Migration complete!</h2>
              <p className="text-muted-foreground max-w-md mx-auto">
                {publishedCount} post{publishedCount !== 1 ? 's' : ''} published to BCH Nostr.
                {failedCount > 0 && ` ${failedCount} failed — you can try again.`}
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 max-w-md mx-auto">
              <Card className="bg-primary/5 border-primary/30">
                <CardContent className="pt-5 text-center">
                  <div className="text-4xl font-black text-primary">{publishedCount}</div>
                  <div className="text-sm text-muted-foreground mt-1">Posts published</div>
                </CardContent>
              </Card>
              <Card className="bg-secondary/50">
                <CardContent className="pt-5 text-center">
                  <div className="text-4xl font-black text-foreground">{config.relayMetadata.relays.filter((r) => r.write).length}</div>
                  <div className="text-sm text-muted-foreground mt-1">Relays reached</div>
                </CardContent>
              </Card>
            </div>

            {/* Relay details */}
            <div className="max-w-md mx-auto text-left">
              <p className="text-xs text-muted-foreground mb-2">Published to these relays:</p>
              <div className="flex flex-wrap gap-1.5">
                {config.relayMetadata.relays.filter((r) => r.write).map((r) => (
                  <Badge key={r.url} variant="outline" className="text-xs font-mono">
                    {r.url.replace('wss://', '').replace(/\/$/, '')}
                  </Badge>
                ))}
              </div>
            </div>

            <div className="space-y-3">
              <p className="text-sm text-muted-foreground">Explore your content on BCH Nostr clients:</p>
              <div className="flex flex-wrap justify-center gap-2">
                {[
                  { label: 'Nostrbch.club', url: 'https://nostrbch.club' },
                  { label: 'Coracle', url: 'https://coracle.social' },
                  { label: 'Snort', url: 'https://snort.social' },
                ].map((c) => (
                  <a key={c.url} href={c.url} target="_blank" rel="noopener noreferrer">
                    <Badge variant="outline" className="cursor-pointer hover:bg-primary/10 hover:border-primary">{c.label} ↗</Badge>
                  </a>
                ))}
              </div>
            </div>

            <Button onClick={reset} variant="outline" className="mt-4">
              Migrate more content
            </Button>
          </div>
        )}
      </main>
    </div>
  );
}

// ── Sub-components ──────────────────────────────────────────────────────────

function StepIndicator({ current }: { current: Step }) {
  const steps: { id: Step; label: string }[] = [
    { id: 'import', label: 'Import' },
    { id: 'preview', label: 'Review' },
    { id: 'publish', label: 'Publish' },
    { id: 'done', label: 'Done' },
  ];
  const order: Step[] = ['welcome', 'import', 'preview', 'publish', 'done'];
  const currentIdx = order.indexOf(current);

  return (
    <ol className="flex items-center gap-1 text-sm">
      {steps.map((s, i) => {
        const stepIdx = order.indexOf(s.id);
        const isDone = stepIdx < currentIdx;
        const isActive = s.id === current || (current === 'publish' && s.id === 'publish');
        return (
          <li key={s.id} className="flex items-center gap-1">
            <span
              className={cn(
                'inline-flex items-center justify-center w-5 h-5 rounded-full text-xs font-bold border',
                isDone ? 'bg-primary border-primary text-primary-foreground'
                  : isActive ? 'border-primary text-primary'
                  : 'border-border text-muted-foreground'
              )}
            >
              {isDone ? '✓' : i + 1}
            </span>
            <span className={cn('hidden sm:inline', isActive ? 'text-foreground font-medium' : 'text-muted-foreground')}>
              {s.label}
            </span>
            {i < steps.length - 1 && <span className="text-border mx-1">›</span>}
          </li>
        );
      })}
    </ol>
  );
}

function HowToGuide({ platform }: { platform: string }) {
  const guides: Record<string, React.ReactNode> = {
    twitter: (
      <ol className="list-decimal list-inside space-y-1.5 text-sm text-muted-foreground">
        <li>Go to <strong className="text-foreground">Settings → Your Account → Download an archive of your data</strong></li>
        <li>Request your archive and wait for the email (can take up to 24 hours)</li>
        <li>Download the ZIP file, extract it, and find <code className="bg-muted px-1 rounded">data/tweets.js</code></li>
        <li>Upload that file here</li>
      </ol>
    ),
    facebook: (
      <ol className="list-decimal list-inside space-y-1.5 text-sm text-muted-foreground">
        <li>Go to <strong className="text-foreground">Settings & Privacy → Settings → Your Facebook Information → Download Your Information</strong></li>
        <li>Set the format to <strong className="text-foreground">JSON</strong>, select only <strong className="text-foreground">Posts</strong>, then click <strong className="text-foreground">Request a Download</strong></li>
        <li>You'll get an email when it's ready — download and unzip the file</li>
        <li>Inside the ZIP, find <code className="bg-muted px-1 rounded">posts/your_posts_1.json</code> and upload it here</li>
        <li>If you have multiple parts (your_posts_2.json, etc.) you can upload them one at a time</li>
      </ol>
    ),
    linkedin: (
      <ol className="list-decimal list-inside space-y-1.5 text-sm text-muted-foreground">
        <li>Go to <strong className="text-foreground">Me → Settings & Privacy → Data Privacy → Get a copy of your data</strong></li>
        <li>Select <strong className="text-foreground">Posts</strong> (or request the full archive)</li>
        <li>Click <strong className="text-foreground">Request archive</strong> — you'll get an email within minutes for posts only</li>
        <li>Download and unzip the file, then find <code className="bg-muted px-1 rounded">Shares.csv</code></li>
        <li>Upload that file here — it contains all your posts and shares</li>
      </ol>
    ),
    bluesky: (
      <div className="space-y-2 text-sm text-muted-foreground">
        <p>Just enter your Bluesky handle above (e.g. <code className="bg-muted px-1 rounded">yourname.bsky.social</code>) and we'll fetch your public posts directly. No export or file download needed!</p>
        <p className="text-xs">Only your original posts are imported — replies are skipped automatically. Up to 200 posts are fetched.</p>
      </div>
    ),
    reddit: (
      <div className="space-y-3 text-sm text-muted-foreground">
        <ol className="list-decimal list-inside space-y-1.5">
          <li>Go to <strong className="text-foreground">reddit.com/settings/data-request</strong> while logged in</li>
          <li>Select <strong className="text-foreground">All data</strong> and click <strong className="text-foreground">Request data</strong> — you'll get an email when ready (can take up to 30 days)</li>
          <li>Download and unzip the archive</li>
          <li>Upload <code className="bg-muted px-1 rounded">posts.csv</code> for your Reddit posts, or <code className="bg-muted px-1 rounded">comments.csv</code> for your comments — or upload both one at a time</li>
        </ol>
        <p className="text-xs">Each post is migrated as "<strong>r/subreddit: Title + Body</strong>". Deleted/removed posts are skipped automatically.</p>
      </div>
    ),
    telegram: (
      <ol className="list-decimal list-inside space-y-1.5 text-sm text-muted-foreground">
        <li>Open <strong className="text-foreground">Telegram Desktop</strong> (version 4.15.2 or later)</li>
        <li>Go to <strong className="text-foreground">Settings → Advanced → Export Telegram Data</strong></li>
        <li>Select the chats you want, set the format to <strong className="text-foreground">JSON</strong>, and click <strong className="text-foreground">Export</strong></li>
        <li>Find the exported <code className="bg-muted px-1 rounded">result.json</code> file and upload it here</li>
        <li>You can also export a single chat: right-click the chat header → <strong className="text-foreground">Export chat history</strong> → JSON</li>
      </ol>
    ),
    mastodon: (
      <ol className="list-decimal list-inside space-y-1.5 text-sm text-muted-foreground">
        <li>Go to <strong className="text-foreground">Preferences → Import and export → Request your archive</strong></li>
        <li>Download the .tar.gz file and extract it</li>
        <li>Find and upload the <code className="bg-muted px-1 rounded">outbox.json</code> file</li>
      </ol>
    ),
    csv: (
      <div className="space-y-2 text-sm text-muted-foreground">
        <p>Export your content as a CSV file. The first column should contain the post text, and optionally the second column a date.</p>
        <p>Example row: <code className="bg-muted px-1 rounded">"Hello world!","2023-01-15"</code></p>
      </div>
    ),
    text: (
      <div className="text-sm text-muted-foreground">
        <p>Paste your posts directly into the text box above, or upload a <code className="bg-muted px-1 rounded">.txt</code> file. Each line becomes a separate Nostr note.</p>
      </div>
    ),
  };

  return guides[platform] ?? <p className="text-sm text-muted-foreground">Export your data from the platform settings and upload the file.</p>;
}

function formatDate(ts: number): string {
  if (!ts) return '';
  try {
    return new Date(ts * 1000).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
  } catch {
    return '';
  }
}
